/**
 * Trends: the trajectory engine (Worker) turned into pictures.
 * Observed TDEE with its 95% band, where your weight is heading at your
 * current or planned intake, and the intake needed to hit your goal date.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { TimeChart } from '../components/TimeChart';
import { Card, Empty, Segmented, Skeleton, StatTile, Spinner } from '../components/ui';
import type { TrajectoryResponse } from '../data/source';
import { addDays, formatDayLong } from '../lib/dates';
import { errorMessage } from '../lib/logger';
import type { DailyMetric } from '../lib/types';
import { massFromKg, massUnit } from '../lib/units';
import { useApp, useAsync } from '../state/app';

type Horizon = 90 | 180 | 365;
const STATUS_TEXT: Readonly<Record<string, string>> = {
  ok: 'Learned from your data',
  low_confidence: 'Early estimate: keep logging',
  insufficient_data: 'Not enough data yet',
};
const OVERRIDE_TEXT: Readonly<Record<string, string>> = {
  wearable: 'Your watch average',
  activity: 'BMR × activity level',
  observed: 'Learned from your data',
  none: 'Your burn estimate',
};

export function TrendsPage(): ReactElement {
  const { source, profile, today, version, energy, settings } = useApp();
  const units = profile.unit_system;
  const [horizon, setHorizon] = useState<Horizon>(180);
  const [planText, setPlanText] = useState<string>('');
  const [plan, setPlan] = useState<number | null>(null);
  const [result, setResult] = useState<TrajectoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(true);
  const history = useAsync<DailyMetric[]>(() => source.listDailyMetrics(addDays(today, -90), today), [source, today, version]);

  useEffect(() => {
    let cancelled: boolean = false;
    setBusy(true);
    // Watch and activity-level modes project with that burn; learn-from-weight uses the observed
    // burn, and the activity estimate until there's enough data.
    const planning: number | null = energy.planning.kcal !== null ? Math.round(energy.planning.kcal) : null;
    const forced: number | null = settings.energy_mode === 'adaptive' ? null : planning;
    const c = energy.comp;
    const current = c.leanMeasured ? { leanMassKg: c.leanMassKg } : c.bodyFatPct !== null ? { bodyFatPct: c.bodyFatPct } : null;
    source.computeTrajectory({ lookbackDays: 42, horizonDays: horizon, planDailyCalories: plan, tdeeOverrideKcal: forced, current })
      .then(async (r) => {
        if (forced === null && r.energy.tdeeStatus === 'insufficient_data' && planning !== null) {
          return source.computeTrajectory({ lookbackDays: 42, horizonDays: horizon, planDailyCalories: plan, tdeeOverrideKcal: planning, current });
        }
        return r;
      })
      .then((r) => { if (!cancelled) { setResult(r); setError(null); } })
      .catch((err: unknown) => { if (!cancelled) { setResult(null); setError(errorMessage(err)); } })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [source, horizon, plan, version, settings.energy_mode, energy.planning.kcal, energy.comp]);

  const r: TrajectoryResponse | null = result;
  const toU = (kg: number): number => massFromKg(kg, units);
  const hist = (history.data ?? []).filter((d) => d.weight_kg !== null).map((d) => ({ day: d.day, y: toU(d.weight_kg as number) }));
  const proj = r?.projection?.points ?? [];
  const projLine = proj.map((p) => ({ day: p.date, y: toU(p.weightKg) }));
  const band = proj.filter((p) => p.weightLowKg !== null && p.weightHighKg !== null).map((p) => ({ day: p.date, lo: toU(p.weightLowKg as number), hi: toU(p.weightHighKg as number) }));
  const targetKg: number | null = r?.requiredIntakeForTarget?.targetWeightKg ?? null;
  const intakeBars = (history.data ?? []).filter((d) => d.meal_count > 0 && d.day >= addDays(today, -42)).map((d) => ({ day: d.day, y: Math.round(d.calories) }));

  const applyPlan = (): void => {
    const v: number = Number(planText);
    setPlan(planText.trim() === '' ? null : Number.isFinite(v) && v >= 800 && v <= 10000 ? Math.round(v) : null);
  };

  if (error !== null && !busy) {
    return (
      <Card title="Trends">
        <Empty title="Can’t compute your trend yet">{error}<br />Log a few weigh-ins and meals across at least a week, then come back.</Empty>
      </Card>
    );
  }

  const e = r?.energy;
  return (
    <div className="stack">
      <div className="tiles">
        <StatTile label="Daily burn used" value={e?.tdeeKcal ? Math.round(e.tdeeKcal).toLocaleString() : '—'} unit="kcal"
          delta={e ? `${e.tdeeStatus === 'override' ? OVERRIDE_TEXT[energy.planning.method] : STATUS_TEXT[e.tdeeStatus] ?? e.tdeeStatus}${e.tdee95Low && e.tdee95High ? ` · ${Math.round(e.tdee95Low)}–${Math.round(e.tdee95High)}` : ''}` : undefined}
          deltaClass={e?.tdeeStatus === 'ok' ? 'good' : 'muted'} />
        <StatTile label="Weekly change" value={r?.trend.weeklyChangeKg !== null && r?.trend.weeklyChangeKg !== undefined ? `${r.trend.weeklyChangeKg > 0 ? '+' : ''}${toU(r.trend.weeklyChangeKg).toFixed(2)}` : '—'} unit={`${massUnit(units)}/wk`}
          delta={r?.trend.weeklyChangePct !== null && r?.trend.weeklyChangePct !== undefined ? `${r.trend.weeklyChangePct.toFixed(2)}% of body weight` : undefined} />
        <StatTile label="Avg intake" value={e?.meanIntakeKcal ? Math.round(e.meanIntakeKcal).toLocaleString() : '—'} unit="kcal" delta={r ? `${Math.round(r.window.intakeCoverage * 100)}% of days logged` : undefined} />
        <StatTile label="Goal needs" value={r?.requiredIntakeForTarget ? Math.round(r.requiredIntakeForTarget.dailyCalories).toLocaleString() : '—'} unit="kcal/day"
          delta={r?.requiredIntakeForTarget ? (r.requiredIntakeForTarget.basis === 'body_fat' && r.requiredIntakeForTarget.targetBodyFatPct !== null
            ? `to reach ${r.requiredIntakeForTarget.targetBodyFatPct}% body fat (about ${toU(r.requiredIntakeForTarget.targetWeightKg).toFixed(1)} ${massUnit(units)}) by ${formatDayLong(r.requiredIntakeForTarget.targetDate)}`
            : `to reach ${toU(r.requiredIntakeForTarget.targetWeightKg).toFixed(1)} ${massUnit(units)} by ${formatDayLong(r.requiredIntakeForTarget.targetDate)}`) : 'set a goal and date in Targets & Profile'} />
      </div>

      <Card title="Where you’re heading" right={
        <>
          <div className="input-row">
            <div className="input-suffix" style={{ width: 170 }}>
              <input className="input" type="number" placeholder="What-if" value={planText} onChange={(ev) => setPlanText(ev.target.value)} onKeyDown={(ev) => { if (ev.key === 'Enter') applyPlan(); }} aria-label="What-if daily calories" />
              <em>kcal</em>
            </div>
            <button type="button" className="btn btn-sm" onClick={applyPlan}>Try</button>
          </div>
          <Segmented<Horizon> options={[{ value: 90, label: '3m' }, { value: 180, label: '6m' }, { value: 365, label: '1y' }]} value={horizon} onChange={setHorizon} />
        </>
      }>
        {busy && !r ? <Skeleton height={320} /> : (
          <>
            <TimeChart height={320}
              lines={[
                { name: 'Weigh-in', color: 'var(--text-3)', points: hist, dots: true, hideLine: true },
                { name: 'Projected', color: 'var(--protein)', points: projLine, width: 3, dashed: true },
              ]}
              band={band.length > 1 ? { color: 'var(--protein)', points: band } : undefined}
              refLines={targetKg !== null ? [{ y: toU(targetKg), label: `goal ${toU(targetKg).toFixed(0)} ${massUnit(units)}`, color: 'var(--ok)' }] : []}
              yFormat={(v) => v.toFixed(0)} emptyText="Log weigh-ins to see a projection" />
            <div className="row" style={{ marginTop: 10, fontSize: 13, fontWeight: 600 }}>
              {busy && <Spinner />}
              {r?.projection && <span className="muted">Projecting at <b>{r.projection.dailyCaloriesUsed ? Math.round(r.projection.dailyCaloriesUsed).toLocaleString() : '—'} kcal/day</b>{plan !== null ? ' (your what-if)' : ' (your average / planned intake)'}.</span>}
              {r?.projection?.targetWeightReachedOn && <span className="good">Goal weight reached around {formatDayLong(r.projection.targetWeightReachedOn)}.</span>}
              {plan !== null && <button type="button" className="link-btn" onClick={() => { setPlan(null); setPlanText(''); }}>reset</button>}
            </div>
          </>
        )}
      </Card>

      <div className="grid cols-2">
        <Card title="Intake vs burn" sub="last 6 weeks">
          <TimeChart height={220} zeroBased emptyText="Log meals to compare"
            bars={{ name: 'Eaten', color: 'var(--energy)', points: intakeBars }}
            band={e?.tdee95Low && e.tdee95High && intakeBars.length > 1 ? { color: 'var(--burn)', points: [{ day: intakeBars[0]!.day, lo: e.tdee95Low, hi: e.tdee95High }, { day: intakeBars[intakeBars.length - 1]!.day, lo: e.tdee95Low, hi: e.tdee95High }] } : undefined}
            refLines={e?.tdeeKcal ? [{ y: Math.round(e.tdeeKcal), label: `TDEE ${Math.round(e.tdeeKcal)}`, color: 'var(--burn)' }] : []}
            yFormat={(v) => Math.round(v).toLocaleString()} />
        </Card>
        <Card title="How this is worked out">
          {r && (r.warnings.length > 0 || r.assumptions.length > 0) && (
            <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 13.5 }}>
              {r.warnings.map((w) => <li key={w} className="warn" style={{ fontWeight: 600 }}>{w}</li>)}
              {r.assumptions.map((a) => <li key={a} className="muted">{a}</li>)}
            </ul>
          )}
          <p className="muted" style={{ fontSize: 13.5, margin: '0 0 10px' }}>
            {settings.energy_mode === 'adaptive'
              ? 'Your burn comes from how your weight trend actually moved against what you logged, not from a formula.'
              : settings.energy_mode === 'wearable'
                ? 'The projection uses your watch-based burn (resting + active + digestion, averaged over recent days). Switch to Learn from my weight in Targets & Profile to check it against your real weight trend.'
                : 'The projection uses your resting burn times your activity level.'}
            {' '}Weight change is converted to energy using body-composition-aware energy density, and burn drifts down as weight drops.
          </p>
          {r && <ol className="cite" style={{ paddingLeft: 18, margin: 0 }}>{r.citations.map((c) => <li key={c.key}>{c.text}</li>)}</ol>}
        </Card>
      </div>
    </div>
  );
}
